export type NotificationAudience = 'admin' | 'vm' | 'both';
export type NotificationType = 'transaction' | 'payment' | 'system';

export type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  audience: NotificationAudience;
  machineId?: string | null;
  orderId?: string | null;
  data?: Record<string, any> | null;
  read: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
};

function getBase(): string {
  // Prefer explicit notifications base, then API base, else same-origin convention
  const base = (
    (process.env.REACT_APP_NOTIFICATIONS_BASE as any) ||
    (process.env.REACT_APP_API_BASE as any) ||
    (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin + '/admin/api'
      : 'http://localhost:3002')
  ).toString();
  return base.replace(/\/$/, '');
}

export async function listNotifications(limit = 50): Promise<Notification[]> {
  const res = await fetch(`${getBase()}/notifications?limit=${encodeURIComponent(String(limit))}`, {
    headers: authHeaders(),
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(json?.message || res.statusText || 'Failed to load notifications');
  // API style is { data, message }
  return (json?.data || []) as Notification[];
}

export async function markNotificationRead(id: string): Promise<void> {
  const res = await fetch(`${getBase()}/notifications/${encodeURIComponent(id)}/read`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = res.statusText;
    try {
      const json = text ? JSON.parse(text) : null;
      message = json?.message || message;
    } catch {}
    throw new Error(message || 'Failed to mark notification read');
  }
}

export function connectNotificationsSse(onEvent: (event: MessageEvent) => void, listeners?: Record<string, (e: MessageEvent) => void>) {
  const url = `${getBase()}/notifications/stream`;
  const es = new EventSource(url);
  const added: Array<[string, (e: MessageEvent) => void]> = [];

  // Generic message handler
  es.onmessage = onEvent as any;

  if (listeners) {
    for (const [evt, handler] of Object.entries(listeners)) {
      es.addEventListener(evt, handler as any);
      added.push([evt, handler]);
    }
  }

  const close = () => {
    for (const [evt, handler] of added) {
      es.removeEventListener(evt, handler as any);
    }
    es.close();
  };

  return { es, close };
}

function authHeaders(extra?: Record<string, string>) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  const headers: Record<string, string> = { ...(extra || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function resolveNotificationPath(n: Notification): string {
  // Decide where to navigate for a given notification
  // Known routes from ProtectedLayout
  const d = n.data || {};
  if (n.type === 'transaction') {
    const id = (n.orderId || d.orderId || d.id || d.transactionId || '').toString();
    if (id) return `/transactions/${id}`;
    return '/transactions';
  }
  if (n.type === 'payment') {
    // Payment settings view page
    return '/settings/payment/view';
  }
  // If machine-related data exists
  const mid = (d.machineId || n.machineId || '').toString();
  if (mid) return `/machines/${mid}`;
  // Fallback to dashboard
  return '/dashboard';
}

export function formatTimeAgo(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = Math.max(0, (now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}
